import type { ReactNode } from 'react';

export interface KPIWidgetProps {
  icon: ReactNode;
  label: string;
  value: string | number;
  trend?: {
    direction: 'up' | 'down' | 'flat';
    percentage: number;
  };
  color?: 'cyan' | 'amber' | 'purple' | 'green' | 'red' | 'blue';
  onClick?: () => void;
}

const COLOR_MAP: Record<string, { text: string; bg: string }> = {
  cyan: { text: 'text-cyber-cyan', bg: 'bg-cyber-cyan/10' },
  amber: { text: 'text-cyber-amber', bg: 'bg-cyber-amber/10' },
  purple: { text: 'text-cyber-purple', bg: 'bg-cyber-purple/10' },
  green: { text: 'text-cyber-green', bg: 'bg-cyber-green/10' },
  red: { text: 'text-red-400', bg: 'bg-red-400/10' },
  blue: { text: 'text-blue-400', bg: 'bg-blue-400/10' },
};

const TREND_ICONS: Record<string, ReactNode> = {
  up: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
    </svg>
  ),
  down: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 4.5l15 15m0 0V8.25m0 11.25H8.25" />
    </svg>
  ),
  flat: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
    </svg>
  ),
};

export default function KPIWidget({ icon, label, value, trend, color = 'cyan', onClick }: KPIWidgetProps) {
  const colors = COLOR_MAP[color] ?? COLOR_MAP.cyan;

  const trendColor = trend
    ? trend.direction === 'up'
      ? 'text-green-400'
      : trend.direction === 'down'
        ? 'text-red-400'
        : 'text-gray-400'
    : '';

  return (
    <div
      onClick={onClick}
      className="stat-card cursor-pointer group h-full flex flex-col justify-between"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
        <div className={`p-2 rounded-lg ${colors.text} ${colors.bg}`}>{icon}</div>
      </div>
      <div className="mt-2">
        <p className="text-2xl font-bold text-gray-100 font-mono">{value}</p>
        {trend && (
          <div className={`flex items-center gap-1 mt-1 text-xs ${trendColor}`}>
            {TREND_ICONS[trend.direction]}
            <span>{trend.percentage.toFixed(1)}%</span>
          </div>
        )}
      </div>
    </div>
  );
}
